import { FileTextIcon } from "@phosphor-icons/react/dist/csr/FileText";
import { HourglassIcon } from "@phosphor-icons/react/dist/csr/Hourglass";
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import type { ReactNode } from "react";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { StatementRenderer } from "@/payroll/ai-script/statement/statement-renderer";
import type { PollingResult } from "./polling";
import type {
  PayrollStatementNotificationRecipient,
  PayrollStatementProjectionBuild,
} from "./types";

const CenteredStatementState = ({
  icon,
  title,
  body,
  isAlert = false,
  isStatus = false,
  failed = false,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  isAlert?: boolean;
  isStatus?: boolean;
  failed?: boolean;
}) =>
  failed ? (
    <ErrorState title={title} description={body} />
  ) : (
    <Empty
      className={emptyStateVariants({ layout: "workspace" })}
      role={isAlert ? "alert" : isStatus ? "status" : undefined}
    >
      <EmptyHeader>
        <EmptyMedia variant="icon">{icon}</EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{body}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );

export const StatementPreview = ({
  recipient,
  projectionBuild,
}: {
  recipient: PayrollStatementNotificationRecipient;
  projectionBuild: PollingResult<PayrollStatementProjectionBuild> | null;
}) => {
  if (
    projectionBuild?.status === "pending" &&
    !projectionBuild.polling_timed_out
  ) {
    return (
      <CenteredStatementState
        icon={<Spinner aria-hidden />}
        isStatus
        title="Building this period's PayPacks"
        body="This preview will appear automatically when the shared build finishes."
      />
    );
  }
  if (projectionBuild?.polling_timed_out) {
    return (
      <CenteredStatementState
        icon={<HourglassIcon aria-hidden />}
        title="PayPack build is taking longer than expected"
        body="Use Check again above the recipient list to resume checking its progress."
      />
    );
  }
  if (projectionBuild?.status === "failed") {
    return (
      <CenteredStatementState
        icon={<WarningCircleIcon aria-hidden />}
        isAlert
        failed
        title="PayPack build failed"
        body={
          projectionBuild.error ?? "Retry the shared PayPack build to continue."
        }
      />
    );
  }
  if (recipient.statement_projection_error) {
    return (
      <CenteredStatementState
        icon={<WarningCircleIcon aria-hidden />}
        isAlert
        failed
        title="PayPack unavailable"
        body={recipient.statement_projection_error}
      />
    );
  }
  if ((recipient.statement_audit_issues?.length ?? 0) > 0) {
    return (
      <CenteredStatementState
        icon={<WarningCircleIcon aria-hidden />}
        title="PayPack needs review"
        body={recipient.statement_audit_issues?.join("; ") ?? "Invalid PayPack"}
      />
    );
  }
  if (!recipient.statement_preview_available) {
    return (
      <CenteredStatementState
        icon={<FileTextIcon aria-hidden />}
        title="No PayPack to preview"
        body="This technician doesn't have a valid PayPack for this period."
      />
    );
  }

  const statement = recipient.statement;
  if (!statement || statement.blocks.length === 0) {
    return (
      <CenteredStatementState
        icon={<FileTextIcon aria-hidden />}
        title="Nothing to show"
        body="This technician's PayPack came back empty for this period."
      />
    );
  }

  return <StatementRenderer statement={statement} />;
};
